/**
 * packageAvatar — ADR-033 phase 2: the exported body becomes the package.
 *
 * The export door writes the avatar with NO clips (World disposes every
 * embedded animation, `VRMAvatarDriver.ts:222-238`) and its textures derived;
 * this seam takes THOSE bytes and, in this order:
 *
 *   1. census — bones, digits per side, tail, declarations, from the FILE;
 *      an animation in the body refuses (the package would be stripped);
 *   2. sha256 of the body — the provenance the record carries, so the hash
 *      names bytes that exist before the record is written into them;
 *   3. compose the record from the author's choices + the census (the
 *      readiness gate, every reason at once);
 *   4. stamp `height`, `package`, `thumbnail` onto the root node.
 *
 * Pure of the filesystem and the scene: bytes in, bytes + record out. The
 * door that owns the picker and the status line calls this; the reopen
 * census reads the result back through `packageCensus` and `parse*Marker`.
 *
 * Depends on: kernel (package, package-census, package-stamp). Depended on
 * by: the package door (main.ts), tests.
 */
import {
  PackageRefusal,
  composePackageRecord,
  packageCensus,
  packageDeclarationSchema,
  stampPackageExtras,
  type AnimatorDocument,
  type PackageCensus,
  type PackageChoices,
  type PackageDeclaration,
  type PackageRecord,
} from '@harryhausen/kernel';

export interface PackageAvatarInputs {
  /** The export door's output: no clips, textures derived, seating/arm-space declared. */
  readonly body: ArrayBuffer;
  readonly doc: AnimatorDocument;
  /** Defaults to `doc.package`. */
  readonly choices?: PackageChoices;
  /** World's family id the rig matched (the library read) — null refuses. */
  readonly family: string | null;
  /** The shelf, e.g. `enhanced-2026`. */
  readonly set: string;
  readonly harryVersion: string;
  /**
   * The starred take as a PNG data URI (≤ 512²). `null` = the take has no
   * bytes in this session — REFUSED once the choices are otherwise ready,
   * because the record names `<id>.png` and a one-file package must carry
   * the card it advertises (Codex P1, #148).
   */
  readonly thumbnail: string | null;
  /** ISO timestamp; defaults to now. */
  readonly packagedAt?: string;
  /** The roster row this package replaces (the panel's `identity.replaces`) — its picker facets carry where the author set none. */
  readonly replaces?: { readonly tags: readonly string[] } | null;
}

export interface PackagedAvatar {
  readonly glb: ArrayBuffer;
  readonly record: PackageRecord;
  readonly census: PackageCensus;
  readonly bodySha256: string;
}

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function packageAvatar(inputs: PackageAvatarInputs): Promise<PackagedAvatar> {
  const census = packageCensus(inputs.body);
  if (census.animations > 0) {
    throw new PackageRefusal([
      `the body carries ${census.animations} animation(s) — World disposes embedded clips, so a package ships none; export for the package with no clips`,
    ]);
  }
  const bodySha256 = await sha256Hex(inputs.body);
  const declarations = Object.keys(census.declarations).filter(
    (k): k is PackageDeclaration => packageDeclarationSchema.safeParse(k).success,
  );
  const choices = inputs.choices ?? inputs.doc.package;
  const noThumbnail = 'the starred take has no PNG in this session — the record names the card, so the package must carry it; capture the take again';
  let record: PackageRecord;
  try {
    record = composePackageRecord({
      doc: inputs.doc,
      ...(choices !== undefined ? { choices } : {}),
      family: inputs.family,
      boneNames: [],
      census: { facts: { bones: census.bones, digits: census.digits, tail: census.tail }, declarations },
      set: inputs.set,
      replaces: inputs.replaces ?? null,
      provenance: { harry: inputs.harryVersion, glbSha256: bodySha256, packagedAt: inputs.packagedAt ?? new Date().toISOString() },
    });
  } catch (e) {
    // The readiness list stays ONE list: a missing card joins the other reasons.
    if (e instanceof PackageRefusal && inputs.thumbnail === null && choices?.heroFavourite !== undefined) throw new PackageRefusal([...e.reasons, noThumbnail]);
    throw e;
  }
  if (inputs.thumbnail === null) throw new PackageRefusal([noThumbnail]);
  const glb = stampPackageExtras(inputs.body, {
    height: { metres: record.heightMeters, provenance: choices?.heightMeters.provenance === 'declared' ? 'explicit' : 'default' },
    record,
    thumbnail: inputs.thumbnail,
  });
  // (`thumbnail` is a string here — the refusal above is the only way past a null.)
  return { glb, record, census, bodySha256 };
}
